import { createReview, createDefaultReviewContent, } from '../models';
import { parseISODate, toISODateString, daysBetween, todayIn } from '../utils/date-utils';
import { deadlineDaysLeft } from './task-scope';
import { computeGoalProgress } from './goal-progress';
import { habitDueDates, dateKey, mondayKey, parseDateKey, scheduleSubject, shiftDateKey } from './habit-schedule';
import { completedOnKey, habitInstancesFor, habitCompletionCounts } from './habit-stats';
import { fitnessPeriodSummary } from './fitness';
export class ReviewService {
    /**
     * Generate a review for the given period
     */
    generateReview(options) {
        const { reviewType, targetDate, tasks, goals, lists, schedules } = options;
        // Calculate period dates
        const derived = this.getPeriodDates(reviewType, targetDate);
        const periodStart = options.periodStart ?? derived.periodStart;
        const periodEnd = options.periodEnd ?? derived.periodEnd;
        // Create base review
        const review = createReview(reviewType, toISODateString(targetDate), periodStart, periodEnd);
        // Generate content
        review.content = this.generateContent(options, periodStart, periodEnd);
        return review;
    }
    /**
     * Get period start and end dates based on review type
     */
    getPeriodDates(reviewType, targetDate) {
        let periodStart;
        let periodEnd;
        switch (reviewType) {
            case 'daily':
                periodStart = targetDate;
                periodEnd = targetDate;
                break;
            case 'weekly': {
                // Monday-first, like every other week in the app (mondayKey). This
                // was a Sunday-start week from getDay(), so a caller that didn't pass
                // its own window got a review a day out of step with its heading.
                const monday = parseDateKey(mondayKey(dateKey(targetDate)) ?? '') ?? targetDate;
                periodStart = monday;
                periodEnd = new Date(monday);
                periodEnd.setDate(monday.getDate() + 6);
                break;
            }
            case 'monthly':
                periodStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
                periodEnd = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
                break;
            default:
                periodStart = targetDate;
                periodEnd = targetDate;
        }
        return {
            periodStart: toISODateString(periodStart),
            periodEnd: toISODateString(periodEnd),
        };
    }
    /**
     * Generate review content
     */
    /**
     * A date field read defensively.
     *
     * `completed_at` and `deadline` were guarded; `updated_at` was not, and
     * `t.updated_at.split(...)` on a task that lacked it threw. That exception
     * propagated to useReviews, whose try/catch replaced the ENTIRE review with
     * `createDefaultReviewContent()` — a productivity score of 0 on a week full
     * of finished work, with nothing on screen to say anything had gone wrong.
     * One malformed record must cost you that record, not the week.
     */
    safeDate(value) {
        if (!value || typeof value !== 'string')
            return null;
        const parsed = parseISODate(value.split('T')[0]);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    generateContent(options, periodStart, periodEnd) {
        const { tasks, goals, lists, schedules, projects = [], habitCompletionCounts = {} } = options;
        const content = createDefaultReviewContent();
        const startDate = parseISODate(periodStart);
        const endDate = parseISODate(periodEnd);
        // Task summary
        // The day it was finished in the user's zone — completedOnKey, the one
        // rule. Reading the date part of the UTC stamp filed an evening's work in
        // the Americas (or a morning's in Asia/Pacific) under the wrong day, and
        // at a period's edge, in the wrong week.
        const completedTasks = tasks.filter(t => {
            if (t.status !== 'completed' || t.is_habit)
                return false;
            const day = completedOnKey(t, options.timezone);
            return !!day && day >= periodStart && day <= periodEnd;
        });
        content.tasks_completed = {
            completed_count: completedTasks.length,
            completed_tasks: completedTasks.map(t => ({
                id: t.id,
                title: t.title,
                completed_at: t.completed_at,
            })),
            stale_count: 0,
            stale_tasks: [],
            upcoming_deadlines: [],
            total_time_spent: completedTasks.reduce((sum, t) => sum + (t.actual_minutes ?? t.estimated_minutes), 0),
        };
        // Find stale tasks (not updated in 7+ days)
        const staleTasks = tasks.filter(t => {
            if (t.status === 'completed')
                return false;
            const lastUpdated = this.safeDate(t.updated_at);
            // No usable timestamp means we can't call it stale — silently skipping
            // it is right, throwing over it was not.
            if (!lastUpdated)
                return false;
            return daysBetween(new Date(), lastUpdated) >= 7;
        });
        content.tasks_completed.stale_count = staleTasks.length;
        content.tasks_completed.stale_tasks = staleTasks.slice(0, 5).map(t => ({
            id: t.id,
            title: t.title,
            last_updated: t.updated_at,
        }));
        // Find upcoming deadlines
        const today = options.today ?? todayIn(options.timezone);
        const upcomingDeadlines = tasks.filter(t => {
            if (t.status === 'completed')
                return false;
            const daysUntil = deadlineDaysLeft(t, today, options.timezone);
            return daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
        });
        content.tasks_completed.upcoming_deadlines = upcomingDeadlines.map(t => ({
            id: t.id,
            title: t.title,
            deadline: t.deadline,
        }));
        // Goal progress. Uses the derived roll-up rather than the legacy
        // Goal.progress field, which was never written and so reported 0% for
        // every goal — that in turn zeroed the goal component of the
        // productivity score below.
        content.goals_progress = goals
            .filter(g => g.status === 'active')
            .map(g => {
            // "As of" the end of the review period, so a historical review
            // reports the progress that was true then, not today's.
            // The completion records when we have them, counted in each goal's
            // own window up to the period's end — the Goals page's reading.
            const computed = computeGoalProgress(g, goals, tasks, projects, options.habitInstances ?? habitCompletionCounts, endDate);
            // Count period completions across ALL three link types, not just
            // tasks that happen to sit in a linked project.
            const linkedTaskIds = new Set(g.linked_task_ids ?? []);
            const linkedHabitIds = new Set(g.linked_habit_ids ?? []);
            const linkedProjectIds = new Set(g.linked_project_ids ?? []);
            return {
                goal_id: g.id,
                title: g.title,
                progress: computed.progress,
                status: g.status,
                linked_projects_count: g.linked_project_ids.length,
                tasks_completed_in_period: completedTasks.filter(t => linkedProjectIds.has(t.project_id) ||
                    linkedTaskIds.has(t.id) ||
                    linkedHabitIds.has(t.id)).length,
            };
        });
        // Habit summary. Completions come from HabitInstance records, not from
        // task status: completing a habit resets it to 'todo' for the next
        // occurrence, so a status filter here matched nothing and every habit
        // reported 0% adherence.
        const habits = tasks.filter(t => t.is_habit);
        const instances = options.habitInstances ?? [];
        content.habits_summary = habits.map(habit => {
            const done = habitInstancesFor(instances, habit.id, periodStart, periodEnd);
            const scheduledCount = this.countScheduledInstances(habit, startDate, endDate);
            const completedCount = done.length;
            return {
                habit_id: habit.id,
                title: habit.title,
                scheduled_count: scheduledCount,
                completed_count: completedCount,
                // Adherence can exceed 100 if a habit was ticked on a day it wasn't
                // scheduled; capping keeps the review readable without hiding it —
                // completed_count still shows the real number.
                completion_rate: scheduledCount > 0
                    ? Math.min(100, (completedCount / scheduledCount) * 100)
                    : 0,
                current_streak: this.currentHabitStreak(instances, habit.id, periodEnd),
            };
        });
        // List summary
        content.lists_summary = lists
            .filter(l => !l.archived)
            .map(l => {
            // `?? []` for the same reason as safeDate: a list without an items
            // array is imperfect data, not a reason to blank the whole review.
            const items = l.items ?? [];
            return {
                list_id: l.id,
                name: l.name,
                pending_count: items.filter(i => !i.completed_at).length,
                completed_count: items.filter(i => i.completed_at).length,
                recent_additions: items
                    .filter(i => !i.completed_at)
                    .slice(0, 3)
                    .map(i => i.text),
            };
        });
        // Calculate productivity score
        content.productivity_score = this.calculateProductivityScore(content, schedules);
        const fitness = options.fitnessWorkouts?.length
            ? fitnessPeriodSummary(options.fitnessWorkouts, periodStart, periodEnd, options.fitnessUnit ?? 'kg')
            : null;
        if (fitness)
            content.fitness = fitness;
        // Generate highlights and areas for improvement
        content.highlights = this.generateHighlights(content);
        content.areas_for_improvement = this.generateAreasForImprovement(content);
        return content;
    }
    /**
     * Count scheduled instances for a habit in a period
     */
    countScheduledInstances(habit, startDate, endDate) {
        // Delegates to the one predicate the planner, the sidebar widget, the
        // streak and Analytics all use. The local version here was a FIFTH copy:
        // it dropped monthly habits, ignored pausing, archiving and start dates,
        // and — because it reached straight into `days_of_week` — threw outright
        // on a weekly habit that had none, taking the whole review with it.
        return habitDueDates(scheduleSubject(habit), dateKey(startDate), dateKey(endDate)).length;
    }
    currentHabitStreak(instances, habitId, asOf) {
        const dates = new Set(instances.filter(i => i.habit_id === habitId).map(i => i.date));
        if (dates.size === 0)
            return 0;
        let streak = 0;
        const cursor = parseISODate(asOf);
        // A habit not yet done today hasn't broken a streak, so allow the run to
        // start at either the last day of the period or the one before it.
        if (!dates.has(toISODateString(cursor))) {
            cursor.setDate(cursor.getDate() - 1);
            if (!dates.has(toISODateString(cursor)))
                return 0;
        }
        while (dates.has(toISODateString(cursor))) {
            streak++;
            cursor.setDate(cursor.getDate() - 1);
        }
        return streak;
    }
    calculateProductivityScore(content, schedules) {
        let score = 50; // Base score
        // Task completion bonus (up to 25 points)
        if (content.tasks_completed.completed_count > 0) {
            score += Math.min(25, content.tasks_completed.completed_count * 2);
        }
        // Habit completion rate (up to 15 points)
        const avgHabitRate = content.habits_summary.length > 0
            ? content.habits_summary.reduce((sum, h) => sum + h.completion_rate, 0) / content.habits_summary.length
            : 0;
        score += avgHabitRate * 0.15;
        // Goal progress bonus (up to 10 points)
        const avgGoalProgress = content.goals_progress.length > 0
            ? content.goals_progress.reduce((sum, g) => sum + g.progress, 0) / content.goals_progress.length
            : 0;
        score += avgGoalProgress * 0.1;
        // Penalties
        if (content.tasks_completed.stale_count > 5) {
            score -= 10;
        }
        return Math.max(0, Math.min(100, Math.round(score)));
    }
    /**
     * Generate highlights
     */
    generateHighlights(content) {
        const highlights = [];
        if (content.tasks_completed.completed_count >= 10) {
            highlights.push(`Completed ${content.tasks_completed.completed_count} tasks!`);
        }
        const highHabitRate = content.habits_summary.filter(h => h.completion_rate >= 80);
        if (highHabitRate.length > 0) {
            highlights.push(`${highHabitRate.length} habits with 80%+ completion rate`);
        }
        const progressingGoals = content.goals_progress.filter(g => g.tasks_completed_in_period > 0);
        if (progressingGoals.length > 0) {
            highlights.push(`Made progress on ${progressingGoals.length} goals`);
        }
        if (content.productivity_score >= 80) {
            highlights.push('Excellent productivity this period!');
        }
        if (content.fitness) {
            const { workouts, records } = content.fitness;
            highlights.push(`${workouts} workout${workouts === 1 ? '' : 's'} at the gym`
                + (records.length ? ` — new best on ${records.map((r) => `${r.name} (${r.weight} ${content.fitness.unit})`).join(', ')}` : ''));
        }
        return highlights;
    }
    /**
     * Generate areas for improvement
     */
    generateAreasForImprovement(content) {
        const areas = [];
        if (content.tasks_completed.stale_count > 3) {
            areas.push(`${content.tasks_completed.stale_count} tasks haven't been updated in a week`);
        }
        const lowHabitRate = content.habits_summary.filter(h => h.completion_rate < 50);
        if (lowHabitRate.length > 0) {
            areas.push(`${lowHabitRate.length} habits below 50% completion`);
        }
        if (content.tasks_completed.upcoming_deadlines.length > 3) {
            areas.push(`${content.tasks_completed.upcoming_deadlines.length} tasks due this week`);
        }
        const stuckGoals = content.goals_progress.filter(g => g.tasks_completed_in_period === 0);
        if (stuckGoals.length > 0) {
            areas.push(`${stuckGoals.length} goals with no recent progress`);
        }
        return areas;
    }
}
/** The period of `type` containing `day` (YYYY-MM-DD), or null for a bad date. */
export function reviewPeriod(type, day) {
    const parsed = parseDateKey(day);
    if (!parsed)
        return null;
    let date;
    let start;
    let end;
    if (type === 'weekly') {
        start = mondayKey(dateKey(parsed)) ?? dateKey(parsed);
        end = shiftDateKey(start, 6);
        date = start;
    }
    else if (type === 'monthly') {
        start = dateKey(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
        end = dateKey(new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0));
        date = start.slice(0, 7);
    }
    else {
        start = end = date = dateKey(parsed);
    }
    return { type, id: `review-${type}-${date}`, date, start, end };
}
/**
 * The stored review for a period. Matched by its key OR its first day, so a
 * review written before ids were shared (the assistant's, dated any day in
 * the period) is still found rather than duplicated.
 */
export function findReview(reviews, period) {
    return reviews.find((r) => r.review_type === period.type && r.date === period.date)
        ?? reviews.find((r) => r.review_type === period.type && r.period_start === period.start);
}
/**
 * A review of the period of `type` containing `day` — the one builder the
 * app and the assistant share. Habit completion counts are taken over the
 * period itself (the assistant counted a monthly review's from a single day).
 */
export function buildReview(type, day, inputs) {
    const period = reviewPeriod(type, day);
    if (!period)
        return null;
    const review = new ReviewService().generateReview({
        ...inputs,
        reviewType: type,
        targetDate: parseDateKey(period.start),
        periodStart: period.start,
        periodEnd: period.end,
        habitCompletionCounts: habitCompletionCounts(inputs.habitInstances ?? [], period.start, period.end),
    });
    return { ...review, id: period.id, date: period.date };
}
//# sourceMappingURL=reviews.js.map